import { asArray, asBoolean, asInteger, asNumber, asRecord, asString } from "../document/validate.ts";
import type { JsonSchema, PropertySchema } from "./schema.ts";

/**
 * A field schema as the game contract carries it (ADR-052): written with fixed key order and read back keeping
 * every keyword, so the subset check judges what was served. An entity type's, an object type's and a game's
 * map schema (ADR-052's amendment of 2026-09-26) are carried alike, with every field kind of the subset, a
 * list's `items` written and read by the same rules (ADR-052's amendment "the other field kinds").
 */

const FORBIDDEN_KEY = "__proto__";

/**
 * A field schema with its properties in declaration order and each property's keywords in a fixed order.
 *
 * @param schema The schema.
 * @returns The plain object.
 */
export function plainSchema(schema: JsonSchema): Record<string, unknown> {
  const properties: Record<string, unknown> = {};
  for (const [name, property] of Object.entries(schema.properties)) {
    properties[name] = plainProperty(property);
  }
  return { type: schema.type, properties, ...(schema.required === undefined ? {} : { required: [...schema.required] }) };
}

/**
 * One property with its keywords in the fixed order: `type`, `title`, `default`, `enum`, `maxLength`,
 * `minimum`, then those of the later kinds (ADR-052's amendment "the other field kinds"), `multiline`, `lookup`
 * (ADR-052's note of 2026-10-02), `unit`,
 * `target`, `items` (by this same rule), `maxItems` and a path's `closeWhen`, so a property using none of the later ones is written as
 * it was before them.
 *
 * @param property The property.
 * @returns The plain object.
 */
function plainProperty(property: PropertySchema): Record<string, unknown> {
  const plain: Record<string, unknown> = { type: property.type };
  for (const keyword of ["title", "default", "enum", "maxLength", "minimum", "multiline", "lookup"] as const) {
    const value = (property as Record<string, unknown>)[keyword];
    if (value !== undefined) {
      plain[keyword] = value;
    }
  }
  if ((property.type === "number" || property.type === "integer") && property.unit !== undefined) {
    plain["unit"] = { symbol: property.unit.symbol, per: property.unit.per };
  }
  if (property.type === "reference") {
    plain["target"] = { kind: property.target.kind, type: property.target.type };
  }
  if (property.type === "path") {
    if (property.maxItems !== undefined) {
      plain["maxItems"] = property.maxItems;
    }
    if (property.closeWhen !== undefined) {
      plain["closeWhen"] = { field: property.closeWhen.field, equals: property.closeWhen.equals };
    }
  }
  if (property.type === "list") {
    plain["items"] = plainProperty(property.items);
    if (property.maxItems !== undefined) {
      plain["maxItems"] = property.maxItems;
    }
  }
  return plain;
}

/**
 * Reads a field schema, keeping every keyword a property carries so the editor's subset check judges it.
 *
 * @param value The value.
 * @param path Path for messages.
 * @returns The schema.
 * @throws Error naming the path when the schema is not an object of properties or a keyword has the wrong
 *   type.
 */
export function readSchema(value: unknown, path: string): JsonSchema {
  const record = asRecord(value, path);
  const type = asString(record["type"], `${path}.type`);
  if (type !== "object") {
    throw new Error(`${path}.type must be "object"`);
  }
  const properties: Record<string, PropertySchema> = {};
  for (const [name, property] of Object.entries(asRecord(record["properties"], `${path}.properties`))) {
    if (name === FORBIDDEN_KEY) {
      throw new Error(`${path}.properties.${FORBIDDEN_KEY} is not an allowed property name`);
    }
    properties[name] = readProperty(property, `${path}.properties.${name}`);
  }
  const required = record["required"] === undefined ? undefined : asArray(record["required"], `${path}.required`).map((item, i) => asString(item, `${path}.required[${i}]`));
  return { type: "object", properties, ...(required === undefined ? {} : { required }) };
}

/**
 * Reads one property schema, checking the type of every keyword it carries and leaving unknown keywords
 * in place for the subset check to reject by name.
 *
 * @param value The value.
 * @param path Path for messages.
 * @returns The property schema.
 * @throws Error naming the path when a keyword has the wrong type.
 */
function readProperty(value: unknown, path: string): PropertySchema {
  const record = asRecord(value, path);
  const out: Record<string, unknown> = { ...record };
  const type = asString(record["type"], `${path}.type`);
  if (record["title"] !== undefined) {
    out["title"] = asString(record["title"], `${path}.title`);
  }
  if (record["enum"] !== undefined) {
    out["enum"] = asArray(record["enum"], `${path}.enum`).map((item, i) => asString(item, `${path}.enum[${i}]`));
  }
  if (record["default"] !== undefined) {
    out["default"] = readDefault(record["default"], type, `${path}.default`);
  }
  if (record["target"] !== undefined) {
    const target = asRecord(record["target"], `${path}.target`);
    out["target"] = { ...target, kind: asString(target["kind"], `${path}.target.kind`), type: asString(target["type"], `${path}.target.type`) };
  }
  if (record["multiline"] !== undefined) {
    out["multiline"] = asBoolean(record["multiline"], `${path}.multiline`);
  }
  if (record["lookup"] !== undefined) {
    out["lookup"] = asString(record["lookup"], `${path}.lookup`);
  }
  if (record["unit"] !== undefined) {
    const unit = asRecord(record["unit"], `${path}.unit`);
    out["unit"] = { ...unit, symbol: asString(unit["symbol"], `${path}.unit.symbol`), per: asNumber(unit["per"], `${path}.unit.per`) };
  }
  if (record["items"] !== undefined) {
    out["items"] = readProperty(record["items"], `${path}.items`);
  }
  if (record["maxItems"] !== undefined) {
    out["maxItems"] = asInteger(record["maxItems"], `${path}.maxItems`);
  }
  if (record["closeWhen"] !== undefined) {
    const close = asRecord(record["closeWhen"], `${path}.closeWhen`);
    out["closeWhen"] = { ...close, field: asString(close["field"], `${path}.closeWhen.field`), equals: asString(close["equals"], `${path}.closeWhen.equals`) };
  }
  return out as unknown as PropertySchema;
}

/**
 * Reads a property's default value in the property's own type.
 *
 * @param value The value.
 * @param type The property type.
 * @param path Path for messages.
 * @returns The default.
 * @throws Error naming the path when the default does not match the property's type.
 */
function readDefault(value: unknown, type: string, path: string): string | number | boolean {
  switch (type) {
    case "string":
      return asString(value, path);
    case "boolean":
      return asBoolean(value, path);
    case "integer":
      return asInteger(value, path);
    case "number":
      return asNumber(value, path);
    default:
      throw new Error(`${path} belongs to a property of unsupported type ${type}`);
  }
}
